import React, { useEffect, useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
} from "@mui/material";
import { toast } from "react-toastify";
import AlertDialog from "../../../components/alertDialog";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import {
    deleteCurrency,
    getCurrencys,
    getCurrencysError,
    getCurrencysStatus,
    getCurrencysSuccess,
    updateStatus,
    selectAllCurrencys
} from "../../../features/currencySlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import AppBarSearch from "../../../global/appBarSearch";
import NoDataTable from "../../../components/noDataTable";
import ParagraphHighliter from "../../../components/paragraphHighliter";
import RestaurantTopBar from "../../../components/restaurantTopBar";
import { ResponsiveProvider } from "../../../hooks/uiHook";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TableActions from "../../../components/tableActions";
const CurrencyManagement = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [localCurrencies, setLocalCurrencies] = useState([]);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [cardId, setCardId] = useState(null);
    const currencies = useSelector(selectAllCurrencys);
    const error = useSelector(getCurrencysError);
    const success = useSelector(getCurrencysSuccess);
    const status = useSelector(getCurrencysStatus);

    const handleClickOpen = (cardId) => {
        setOpen(true);
        setCardId(cardId);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        navigate("add");
    };

    const handleModify = (data) => {
        navigate("modify", { state: { currency: data } });
    };

    useEffect(() => {
        if (status === "fetchData") setLocalCurrencies(currencies);
    }, [currencies, status]);

    useEffect(() => {
        dispatch(getCurrencys());
    }, [dispatch,]);

    const filteredCurrenciesMemo = useMemo(() => {
        let filtered = localCurrencies || [];

        if (search) {
            filtered = filtered.filter((product) =>
                (product.name || "").toLowerCase().includes(search.toLowerCase()) ||
                (product.code || "").toLowerCase().includes(search.toLowerCase()) ||
                (product.symbol || "").toLowerCase().includes(search.toLowerCase())
            );
        }

        return filtered;
    }, [localCurrencies, search]);

    useEffect(() => {
        if (status === "deleteSuccess") {
            toast.success(success);
            setLocalCurrencies((prev) => prev.filter((item) => item._id !== cardId));
            dispatch(updateStatus());
        } else if (status === "deleteError") {
            toast.error(error);
        }
    }, [cardId, dispatch, error, status, success, filteredCurrenciesMemo]);

    return (
        <div className="main-application">
            <RestaurantTopBar />
            <ResponsiveProvider maxWidth={900}>
                <AppBarSearch
                    searchText={t("search.byNameCodeSymbol")}
                    handleSearch={(e) => setSearch(e.target.value)}
                    title={t("pages.currencies")}
                    buttonTitle={t("actions.addCurrency")}
                    handleSubmit={handleSubmit}
                />
            </ResponsiveProvider>
            <main>
                <Box sx={{ my: 2 }}>
                    <TableContainer>
                        <Table sx={{ backgroundColor: colors.primary[400] }}>
                            <TableHead sx={{ backgroundColor: colors.primary[700] }}>
                                <TableRow>
                                    <TableCell>{t("table.name")}</TableCell>
                                    <TableCell>{t("table.code")}</TableCell>
                                    <TableCell>{t("table.symbol")}</TableCell>
                                    <TableCell align="right">
                                        {t("table.actions")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {status === "loading" ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            <Loading size={8} />
                                        </TableCell>
                                    </TableRow>
                                ) : status === "fetchError" ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            <Error>{error}</Error>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredCurrenciesMemo && filteredCurrenciesMemo.length > 0 ? (
                                    filteredCurrenciesMemo.map((currency, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <ParagraphHighliter
                                                    title={currency.name}
                                                    search={search}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <ParagraphHighliter
                                                    title={currency.code}
                                                    search={search}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <ParagraphHighliter
                                                    title={currency.symbol}
                                                    search={search}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableActions
                                                    actions={[
                                                        {
                                                            type: "edit",
                                                            color: "warning",
                                                            label: t("common.edit"),
                                                            onClick: () => handleModify(currency),
                                                        },
                                                        {
                                                            type: "delete",
                                                            color: "error",
                                                            label: t("common.delete"),
                                                            onClick: () => handleClickOpen(currency._id),
                                                        },
                                                    ]}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <NoDataTable cols={2} />
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </main>
            <AlertDialog
                open={open}
                handleClose={() => setOpen(false)}
                message={"Voulez-vous supprimer cette devise ?"}
                cardId={cardId}
                deleteData={() => {
                    dispatch(deleteCurrency(cardId));
                    setOpen(false);
                }}
            />
        </div>
    );
};

export default CurrencyManagement;
